import type { CheckBoxEventValue } from '@interstate/components/CheckBox';
import { CheckBox } from '@interstate/components/CheckBox';
import { Table } from '@interstate/components/Table';
import { Typography } from '@interstate/components/Typography';
import * as dueAtSigningActionCreators from '@makemydeal/dr-dash-store/actions/dueAtSigningActionCreators';
import * as dueAtSigningSelectors from '@makemydeal/dr-dash-store/selectors/dueAtSigningSelectors';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import type { DasOverridesFlagsTypes, DealHistory } from '@makemydeal/dr-dash-types';
import type { FC } from 'react';
import { useMemo } from 'react';
import { createDealRootSelector } from '../offerCompareColumn/hooks/createDealRootSelector';

import { Box } from '@interstate/components/Box';
import type { InterstateOnChangeCallback, InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import { formatDollarsAndCents } from '@makemydeal/dr-common-utils';
import { useDispatch, useSelector } from 'react-redux';
import { amountDueAtSigningContainerStyle } from '../dealSummary/common/ComponentLabelStyle';
import { dealLockHooks } from '../hooks';

type TableData = {
    extraData: string;
    id: number;
    key: number;
    fieldName: string;
    item: string;
    capitalize?: boolean;
    amount: string;
};

type DueAtSigningProps = {
    deal?: DealHistory;
};

const checkboxFields = ['firstPaymentCapitalized', 'feesCapitalized', 'taxesCapitalized'];

const AmountDueAtSigningTable: FC<DueAtSigningProps> = ({ deal }) => {
    const dispatch = useDispatch();
    const useDealRootSelector = createDealRootSelector(deal, 'AmountDueAtSigningTable');
    const payment = useDealRootSelector(dueAtSigningSelectors.getDasDefaultPayment, 'dasDefaultPayment');
    const dasOverrides = useDealRootSelector(dueAtSigningSelectors.getDasOverrides, 'dasOverrides');
    const isDasLocked = useSelector(dueAtSigningSelectors.getIsDasLocked);
    const isLocked = dealLockHooks.useIsDealLocked();

    const capCostReduction = payment?.capCostReduction;
    const upfrontAddonsTotal = payment?.upfrontAddonsTotal;
    const secDeposit = payment?.secDeposit;
    const diaDueAtSigning = payment?.diaDueAtSigning;
    const disabled = isDasLocked || isLocked;
    const firstPayment = payment?.firstPayment;
    const isOnePayLease = useDealRootSelector(offerReduxSelectors.isOnePayLease, 'onePayLease');
    const baseMonthlyPaymentsTotal = useDealRootSelector(offerReduxSelectors.getBaseMonthlyPaymentTotal, 'baseMonthlyPaymentTotal');
    const firstOrTotalPayment = isOnePayLease ? baseMonthlyPaymentsTotal : firstPayment;
    const upfrontTaxTotal = payment?.upfrontTaxTotal;
    const upfrontFeesTotal = payment?.upfrontFeesTotal;

    const handleCheckboxChange: InterstateOnChangeCallback<CheckBoxEventValue> = (
        event: InterstateOnChangeEvent<CheckBoxEventValue>
    ) => {
        const input = event.target.value || {};
        const dueAtSigningDispatch = dueAtSigningActionCreators.updateDasChanges(input.checkboxValue);
        dispatch(dueAtSigningDispatch(input.checked));
    };

    const columns = [
        {
            className: 'item',
            dataIndex: 'item',
            title: 'Item',
            width: 'auto',
            render: (text: string) => (
                <Box component="span" sx={{ color: '#6D6D6D' }}>
                    {text}
                </Box>
            )
        },
        {
            className: 'capitalize',
            dataIndex: 'capitalize',
            align: 'center' as const,
            width: 'auto',
            render: (_value: any, record: TableData) => {
                if (checkboxFields.includes(record.fieldName)) {
                    const checkValue = dasOverrides[record.fieldName as keyof DasOverridesFlagsTypes] || false;

                    return (
                        <Box component="div" sx={{ display: 'flex', justifyContent: 'center' }}>
                            <CheckBox
                                checked={checkValue}
                                value={record.fieldName}
                                onChange={handleCheckboxChange}
                                disabled={disabled}
                            />
                        </Box>
                    );
                }
                return null;
            },
            title: (
                <Box component="span" sx={{ textAlign: 'center', display: 'block' }}>
                    Capitalize
                </Box>
            )
        },
        {
            align: 'right' as const,
            className: 'amount',
            dataIndex: 'amount',
            title: 'Amount',
            width: 'auto'
        }
    ];

    const tableData: TableData[] = useMemo(
        () => [
            {
                extraData: 'Column not to be shown',
                id: 0,
                key: 0,
                item: isOnePayLease ? 'Total of Payments' : 'First Month Payment',
                fieldName: isOnePayLease ? 'totalOfPayments' : 'firstPaymentCapitalized',
                capitalize: true,
                amount: formatDollarsAndCents(firstOrTotalPayment)
            },
            {
                extraData: 'Column not to be shown',
                id: 1,
                key: 1,
                item: 'Upfront Fees',
                fieldName: 'feesCapitalized',
                capitalize: true,
                amount: formatDollarsAndCents(upfrontFeesTotal)
            },
            {
                extraData: 'Column not to be shown',
                id: 2,
                key: 2,
                item: 'Upfront Taxes',
                fieldName: 'taxesCapitalized',
                capitalize: true,
                amount: formatDollarsAndCents(upfrontTaxTotal)
            },
            {
                extraData: 'Column not to be shown',
                id: 3,
                key: 3,
                item: 'Capitalized Cost Reduction',
                fieldName: 'capCostReduction',
                capitalize: false,
                amount: formatDollarsAndCents(capCostReduction)
            },
            {
                extraData: 'Column not to be shown',
                id: 4,
                key: 4,
                item: 'Additional Upfronts',
                fieldName: 'addUpfronts',
                capitalize: false,
                amount: formatDollarsAndCents(upfrontAddonsTotal)
            },
            {
                extraData: 'Column not to be shown',
                id: 5,
                key: 5,
                item: 'Security Deposit',
                fieldName: 'securityDeposit',
                capitalize: false,
                amount: formatDollarsAndCents(secDeposit)
            },
            {
                extraData: 'Column not to be shown',
                id: 6,
                key: 6,
                item: 'Total',
                fieldName: 'total',
                amount: formatDollarsAndCents(diaDueAtSigning)
            }
        ],
        [
            isOnePayLease,
            firstOrTotalPayment,
            upfrontFeesTotal,
            upfrontTaxTotal,
            capCostReduction,
            upfrontAddonsTotal,
            secDeposit,
            diaDueAtSigning
        ]
    );

    return (
        <div style={amountDueAtSigningContainerStyle} data-testid="amount-due-at-signing-container">
            <Typography tag="span" variant="h4" data-testid="amount-due-at-signing-title">
                Amount Due At Signing
            </Typography>
            <Table
                columns={columns}
                data={tableData}
                id="TableRowClick"
                data-testid="amount-due-at-signing-table"
                tableLayout="fixed"
            />
        </div>
    );
};
export default AmountDueAtSigningTable;
